import { PaymentType } from "@/Common/Models/Portal/MedfarPortalEnums";

class CardInfo {
    public paymentType: PaymentType;
    public nicePaymentType: string;
    public expression: RegExp;
    public lengths: number[];
    public inputMask: string;
    public codeName: string;
    public codeLength: number;

    constructor(fields?: Partial<CardInfo>) {
        (Object as any).assign(this, fields);
    }
}

/**
 * Provides methods to get and validate card numbers and types
 *
 * CardInfo values and matching copied and modifed from
 * [credit-card-type v8.3.0]{@link https://github.com/braintree/credit-card-type/tree/v8.3.0}
 *
 * @summary Provides methods to get and validate card numbers and types
 * @see [credit-card-type v8.3.0]{@link https://github.com/braintree/credit-card-type/tree/v8.3.0}
 * @license MIT
 * @copyright Copyright (c) 2009-2018 Braintree, a division of PayPal, Inc.
 */
class CreditCardHelper {
    private cardCheckOrder: CardInfo[] = [
        new CardInfo({
            paymentType: PaymentType.Visa,
            nicePaymentType: "Visa",
            expression: /^4/,
            lengths: [16, 18, 19],
            inputMask: "9999 9999 9999 9999[ 999]",
            codeName: "CVV",
            codeLength: 3
        }),
        new CardInfo({
            paymentType: PaymentType.Mastercard,
            nicePaymentType: "Mastercard",
            expression: /^(?:5[1-5]|2720|222[1-5]|27[0-1]|22[3-9]|2[3-6])/,
            lengths: [16],
            inputMask: "9999 9999 9999 9999",
            codeName: "CVC",
            codeLength: 3
        }),
        new CardInfo({
            paymentType: PaymentType.Amex,
            nicePaymentType: "American Express",
            expression: /^3(?:4|7)/,
            lengths: [15],
            inputMask: "9999 999999 99999",
            codeName: "CID",
            codeLength: 4
        }),
        new CardInfo({
            paymentType: PaymentType.Discover,
            nicePaymentType: "Discover",
            expression: /^6(?:011|5|4[4-9])/,
            lengths: [16, 19],
            inputMask: "9999 9999 9999 9999[ 999]",
            codeName: "CID",
            codeLength: 3
        }),
        new CardInfo({
            paymentType: PaymentType.DinersClub,
            nicePaymentType: "Diners Club",
            expression: /^3(?:6|8|9|0[0-5])/,
            lengths: [14, 16, 19],
            inputMask: "9999 999999 9999[ 99999]",
            codeName: "CVV",
            codeLength: 3
        }),
        new CardInfo({
            paymentType: PaymentType.Jcb,
            nicePaymentType: "Jcb",
            expression: /^(?:2131|1800|352[8-9]|35[3-8]\d)/,
            lengths: [16, 17, 18, 19],
            inputMask: "9999 9999 9999 9999[ 999]",
            codeName: "CVV",
            codeLength: 3
        })
    ];

    private clone(cardInfo: CardInfo): CardInfo {
        let dupe = JSON.parse(JSON.stringify(cardInfo));
        dupe.expression = cardInfo.expression;
        return dupe;
    }

    /**
     * Get the card information for a given credit card number
     *
     * @param {string} somebody The credit card number.
     * @return {CardInfo} The card info for the given number.
     */
    public getPossibleCardInfo(cardNumber: string): CardInfo {
        if (IsNullOrWhiteSpace(cardNumber)) return null;

        for (let cardInfo of this.cardCheckOrder) {
            if (cardInfo.expression.test(cardNumber) && cardNumber.length <= cardInfo.lengths.max())
                return this.clone(cardInfo);
        }
    }

    /**
     * Validate a given credit card number.
     *
     * @param {string} somebody The credit card number to validate.
     * @param {CardInfo} [cardInfo] Specify the card info to use; otherwise the given number will be used to determine the type.
     * @return {boolean} True, if the card number is valid; otherwise, false.
     */
    public validateCardNumber(cardNumber: string, cardInfo?: CardInfo): boolean {
        // validate that the card type is valid and the length matches
        cardInfo = cardInfo || this.getPossibleCardInfo(cardNumber);
        if (!cardInfo || !cardInfo.lengths.contains(cardNumber.length)) return false;

        // then validate the checksum using the Luhn algorithm
        // the algorithm multiplies every second number from the
        // right - reversing the card number is the simplest approach
        let numbers: number[] = (Array as any).from(cardNumber)
            .reverse()
            .map(p => parseInt(p));

        let total = 0;
        for (let i = 0; i < numbers.length; i++) {
            if (i & 1) {
                // Double every second digit and add the resulting digits to
                // the running total.
                const number = numbers[i] * 2;

                // for doubled results 10 - 18, the sum of the digits is
                // the doubled value - 9 (e.g 14 = 1 + 4 = 5 | 14 - 9 = 5)
                if (number > 9) {
                    total += (number - 9);
                }
                else {
                    total += number;
                }
            }
            else {
                total += numbers[i];
            }
        }

        // validate that 10 minues the ones digit equals the checksum
        return (total % 10) === 0;
    }
}

export { CreditCardHelper };
